/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { RichText } from '@/components/common/RichText';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Send, Trash2, Plus, ShieldAlert, Bot, User as UserIcon, Loader2, MessagesSquare } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { timeAgo } from '@/lib/utils-cat';

const SUGGESTIONS = [
  { it: 'Interazioni tra warfarin e amiodarone', en: 'Interactions between warfarin and amiodarone' },
  { it: 'Dosaggio pediatrico paracetamolo per fascia di peso', en: 'Pediatric paracetamol dosing by weight' },
  { it: 'Protocollo sepsi – Hour-1 bundle', en: 'Sepsis Hour-1 bundle protocol' },
  { it: 'Scala Braden per il rischio lesioni da pressione', en: 'Braden scale for pressure injury risk' },
  { it: 'Calendario vaccinale adulto secondo PNPV', en: 'Adult vaccination schedule per PNPV' },
];

const AIChatPage = () => {
  const { t, i18n } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  const loadSessions = async () => {
    try {
      const res = await api.get('/ai/sessions');
      setSessions(res.data || []);
    } catch (err) {
      console.error('AIChat: failed to load sessions', err);
    }
  };

  const loadMessages = useCallback(async (sid) => {
    if (!sid) { setMessages([]); return; }
    try {
      const res = await api.get(`/ai/sessions/${sid}/messages`);
      setMessages(res.data || []);
      setTimeout(scrollToBottom, 50);
    } catch (err) {
      console.error('AIChat: failed to load messages', err);
    }
  }, []);

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { loadMessages(sessionId); }, [sessionId, loadMessages]);

  const newChat = () => { setSessionId(null); setMessages([]); setText(''); setShowSidebar(false); };

  const send = async (customText) => {
    const messageText = (customText || text).trim();
    if (!messageText || sending) return;
    setSending(true);
    // Optimistic
    const optimistic = { id: 'temp-' + Date.now(), role: 'user', content: messageText, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, optimistic]);
    setText('');
    setTimeout(scrollToBottom, 50);
    try {
      const res = await api.post('/ai/chat', { message: messageText, session_id: sessionId });
      const newSid = res.data.session_id;
      if (!sessionId) setSessionId(newSid);
      setMessages(prev => [...prev.filter(m => m.id !== optimistic.id), { ...optimistic, id: optimistic.id.replace('temp-', 'usr-') }, res.data.message]);
      setTimeout(scrollToBottom, 50);
      // Refresh sessions list to show new one
      loadSessions();
    } catch (e) {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      toast.error(e?.response?.data?.detail || t('errorOccurred'));
    } finally { setSending(false); }
  };

  const removeSession = async (sid, e) => {
    e && e.stopPropagation();
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await api.delete(`/ai/sessions/${sid}`);
      if (sid === sessionId) newChat();
      loadSessions();
      toast.success(t('delete'));
    } catch { toast.error(t('errorOccurred')); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestionText = (s) => (i18n.language === 'it' ? s.it : s.en);

  return (
    <div className="flex flex-col lg:flex-row gap-3 min-h-[calc(100vh-12rem)]">
      {/* Sidebar with sessions */}
      <aside className={`${showSidebar ? 'block' : 'hidden'} lg:block lg:w-[240px] shrink-0`}>
        <div className="flex items-center gap-2 mb-2">
          <Button size="sm" onClick={newChat} className="w-full gap-1" data-testid="ai-new-chat-button">
            <Plus className="h-4 w-4" /> {t('newChat')}
          </Button>
        </div>
        <div className="space-y-1 max-h-[60vh] overflow-y-auto scrollbar-thin">
          {sessions.length === 0 && <div className="text-xs text-muted-foreground px-2 py-3">{t('noConversations')}</div>}
          {sessions.map(s => (
            <button key={s.session_id} onClick={() => { setSessionId(s.session_id); setShowSidebar(false); }}
              className={`w-full text-left p-2 rounded-xl border ${s.session_id === sessionId ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border hover:bg-secondary'}`}
              data-testid="ai-session-item">
              <div className="flex items-start gap-2">
                <MessagesSquare className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{s.title || 'Chat'}</div>
                  <div className="text-[10px] text-muted-foreground">{timeAgo(s.last_at, t)} · {s.count} msg</div>
                </div>
                <button onClick={(e) => removeSession(s.session_id, e)} className="opacity-60 hover:opacity-100 hover:text-destructive" data-testid="ai-session-delete">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold tracking-tight">{t('aiAssistant')}</h1>
              <p className="text-xs text-muted-foreground">{t('aiPoweredBy')}</p>
            </div>
          </div>
          <Button size="sm" variant="secondary" className="lg:hidden" onClick={() => setShowSidebar(v => !v)} data-testid="ai-toggle-sidebar">
            <MessagesSquare className="h-4 w-4" />
          </Button>
        </div>

        {/* Disclaimer banner */}
        <div className="mb-3 p-3 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 flex items-start gap-2" data-testid="ai-disclaimer">
          <ShieldAlert className="h-4 w-4 text-yellow-700 dark:text-yellow-400 mt-0.5 shrink-0" />
          <div className="text-[11px] text-yellow-800 dark:text-yellow-200 leading-relaxed">
            {t('aiDisclaimer')}
          </div>
        </div>

        <Card className="flex-1 flex flex-col bg-card border border-border rounded-2xl overflow-hidden">
          <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-3 min-h-[300px] bg-secondary/20">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center py-8">
                <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div className="font-display font-semibold text-lg">{t('aiWelcome')}</div>
                <div className="text-sm text-muted-foreground mt-1 max-w-md px-4">{t('aiWelcomeDesc')}</div>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-xl w-full px-3">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={typeof s === 'string' ? s : (s?.key || s?.text || `sugg-${i}`)} onClick={() => send(suggestionText(s))}
                      className="text-left text-xs p-2.5 rounded-xl border border-border bg-card hover:bg-secondary/60 transition"
                      data-testid={`ai-suggestion-${i}`}>
                      <Sparkles className="inline h-3 w-3 mr-1 text-primary" />{suggestionText(s)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => {
              const isUser = m.role === 'user';
              return (
                <motion.div key={m.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
                  {!isUser && (
                    <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Bot className="h-4 w-4" />
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${isUser ? 'bg-primary text-primary-foreground rounded-tr-md whitespace-pre-wrap' : 'bg-card border border-border rounded-tl-md'}`} data-testid={isUser ? 'ai-msg-user' : 'ai-msg-assistant'}>
                    {isUser ? m.content : <RichText text={m.content} />}
                  </div>
                  {isUser && (
                    <div className="h-8 w-8 rounded-full bg-secondary text-foreground flex items-center justify-center shrink-0">
                      <UserIcon className="h-4 w-4" />
                    </div>
                  )}
                </motion.div>
              );
            })}
            {sending && (
              <div className="flex gap-2 items-center">
                <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
                <div className="text-xs text-muted-foreground">{t('aiThinking')}</div>
              </div>
            )}
          </div>
          <div className="p-2 border-t border-border bg-card flex items-end gap-2">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('aiInputPlaceholder')}
              rows={1}
              className="resize-none min-h-[40px] max-h-[140px]"
              data-testid="ai-input"
            />
            <Button size="icon" onClick={() => send()} disabled={sending || !text.trim()} data-testid="ai-send-button">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AIChatPage;
